import {
  AppType,
  Provider as GadgetProvider,
  useGadget,
} from "@gadgetinc/react-shopify-app-bridge";
import { NavMenu } from "@shopify/app-bridge-react";
import { useEffect, useState, lazy, Suspense } from "react";
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
// Lazy-loaded route components for better performance
/**
 * The dashboard route ALSO waits on the async Polaris component stylesheet.
 *
 * web/main.jsx now loads only the --p-* TOKEN layer render-blocking; the 437 kB
 * component sheet is attached at runtime by web/utils/polarisCss.js, which exports
 * a `polarisCssReady` promise. Making that promise part of THIS lazy() closes the
 * flash window by construction: React cannot commit <Index/> until both the route
 * chunk and the stylesheet have landed, so the dashboard's first painted frame is
 * already styled. Without it the route could mount against an unstyled Polaris
 * layer for however long the CSS request takes.
 *
 * ⚠️ Promise.all, and it must NOT reject-cascade. polarisCss.js resolves its promise
 * on `error` and on a 3 s timeout as well as on `load` — a CSS 404 degrades to
 * unstyled skeletons, it never hangs the admin — so this Promise.all cannot be left
 * pending by a failed stylesheet. Do not "improve" that into a rejecting promise:
 * a rejection here would make the whole dashboard chunk fail to load.
 *
 * ⚠️ THIS IS NOT THE ONLY IMPORT OF polarisCss ANY MORE, AND IT IS NOT WHAT LOADS THE
 * SHEET. There is now an unconditional module-scope `import("../utils/polarisCss")` just
 * below the route warm-ups in this file — see the long note there. It runs at MODULE-EVAL,
 * whereas a lazy() factory does not run until React renders the route, so on this realm the
 * request is already in flight by the time this factory runs. Both are wanted, and they are
 * doing two different jobs:
 *   • the module-scope import LOADS + ATTACHES the sheet, on every route, in every realm;
 *   • this Promise.all GATES <Index/>'s first commit on it, which is what prevents a
 *     flash of unstyled Polaris on the heaviest screen.
 * Loading is not gating. Deleting this leg because "the sheet is already loading" would
 * bring the flash back, and deleting the module-scope one because "the factory imports it"
 * would leave every non-lazy route unstyled again.
 *
 * ⚠️ Still written as a DYNAMIC import rather than a static top-level one: a static import
 * would put polarisCss.js in the entry chunk's own static graph. It stays dynamic so that
 * both sites resolve to the same lazily-fetched module, which polarisCss.js then memoises
 * (`data-ns-polaris-css-loaded` + a cached promise), so they cannot race into two <link>s.
 *
 * `[m]` destructures the ROUTE module out of the pair; the CSS promise resolves to
 * nothing useful and is awaited purely for its timing.
 */
const Index = lazy(() =>
  Promise.all([
    import("../routes/index"),
    import("../utils/polarisCss").then((m) => m.polarisCssReady),
  ]).then(([m]) => m)
);
/**
 * ⚠️ THE EDITOR NEEDS `polarisCssReady` TOO, AND FOR A WHILE IT DID NOT HAVE IT.
 *
 * The Polaris COMPONENT stylesheet (~287 kB) is not loaded by a <link rel="stylesheet"> in
 * the HTML — `vite-plugins/ns-critical-assets.mjs` injects only a `rel="preload"`, which
 * DOWNLOADS the sheet and applies nothing. The single thing that turns it into an applied
 * stylesheet is `web/utils/polarisCss.js:102`, which sets `link.rel = "stylesheet"` and
 * appends it.
 *
 * That module was imported from exactly ONE place: the `Index` lazy() above. And `/popup`
 * runs in the App Bridge MODAL'S OWN IFRAME REALM, with its own document and its own module
 * cache — so the dashboard having loaded it does nothing for the editor. Result: every
 * Polaris REACT component in the campaign editor (RuleEditor's Select, TextField, Labelled,
 * Choice, Popover…) rendered COMPLETELY UNSTYLED — native selects with full-size chevrons,
 * duplicated labels, blown-out layout — while the sheet sat downloaded and unused.
 *
 * Reported from the live dev admin on the Segment & Targeting screen; introduced by the
 * CSS split in 6009ba5c. It is invisible on the dashboard, which is why it survived: the
 * one route that DID load it is the one everybody looks at.
 *
 * ⚠️ Same two constraints as the Index lazy() above, for the same reasons:
 *   • Promise.all, and it must NOT reject-cascade — polarisCss.js resolves on `error` and on
 *     a 3 s timeout as well as on `load`, so a CSS 404 degrades to unstyled rather than
 *     hanging the editor. Do not "improve" that into a rejecting promise.
 *   • The import stays DYNAMIC, so polarisCss.js is not welded into the entry chunk's static
 *     graph and both import sites resolve to the same memoised module.
 *
 * ⚠️ BELT AND BRACES, ON PURPOSE — and the realm split is what forces it.
 * The bare `import("../utils/polarisCss")` at module scope below runs in whichever realm
 * evaluates this file, so when the merchant lands directly on /popup inside the modal iframe
 * it attaches the sheet there without waiting for this factory. That covers LOADING. This
 * Promise.all covers ORDERING: it is the only thing that stops the editor committing its
 * first frame before the sheet has applied. Neither one subsumes the other, and the bug this
 * whole block documents was caused by assuming one site was enough — do not "de-duplicate"
 * them back down to one.
 *
 * `[m]` destructures the ROUTE module out of the pair; the CSS promise is awaited purely
 * for its timing.
 */
const Popup = lazy(() =>
  Promise.all([
    import("../routes/popup"),
    import("../utils/polarisCss").then((m) => m.polarisCssReady),
  ]).then(([m]) => m)
);

/**
 * Is this document load the Shopify App Pricing return?
 *
 * Read from window.location at module scope on purpose. The return is always a
 * FULL COLD BOOT (routes/billing.jsx breaks out of the iframe with
 * open(url, "_top")), so the landing path at module-eval time is a fact about
 * this page load, not state that can change.
 *
 * ⚠️ EXACT match, and it must stay exact. An earlier draft used
 * `endsWith("/welcome")`, reasoning that GADGET_PUBLIC_SHOPIFY_APP_URL can carry
 * a pathname. That rationale was wrong: createBrowserRouter below is created
 * with NO basename, so react-router matches the raw pathname — a prefixed
 * "/app/welcome" would not match the /welcome route at all and would land on
 * Error404. `endsWith` therefore only widened this to paths that CANNOT be the
 * welcome route, while disagreeing with the two other /welcome predicates in the
 * codebase (AuthenticatedApp below, and BILLING_EXEMPT_PATHS in ShopProvider),
 * both of which are exact. Three predicates for one concept must agree, or the
 * gates disagree and the seam this change removes comes back.
 *
 * ⚠️ Deliberately NOT keyed on the `plan_handle` / `charge_id` query params.
 * Those are merchant-typeable on any route (welcome.jsx flags plan_handle as
 * untrusted), and welcome.jsx strips them mid-flight in its `finally` — so a
 * param-derived check would swap the loading screen out from under the merchant
 * partway through, which is the exact remount this change removes.
 */
const IS_PLAN_ACTIVATION_RETURN =
  typeof window !== "undefined" && window.location.pathname === "/welcome";

/**
 * Chunk warm-ups. These are bare side-effect imports — the lazy() declarations
 * above are what actually mount the routes. Being bare, they run at MODULE-EVAL
 * time: before auth resolves, before the router exists, before any route renders.
 *
 * ⚠️ WARM ONLY THE ROUTE THIS DOCUMENT WILL ACTUALLY RENDER.
 *
 * `import("../routes/popup")` used to sit on the else-branch. It is a TWO-LEVEL
 * cascade — popup.jsx has its own module-scope warm-ups for ../components/Preview
 * and ../components/PopupSettings — so one line here downloaded the entire
 * campaign editor on every boot of every route.
 *
 * It was removed 2026-08-16 because on the path that matters it CANNOT DO WHAT IT
 * LOOKS LIKE IT DOES. The editor is normally opened as an App Bridge
 * <Modal src="/popup?campaignId=…"> (routes/index.jsx:896-905, and again in
 * templates.jsx), which is a separate iframe with its own document and its own JS
 * realm — confirmed by the contentWindow.postMessage plumbing at index.jsx:380,876,886
 * and the origin guard at templates.jsx:185. A module cache is per-realm, so warming
 * popup.jsx HERE cannot make the modal mount any faster; it only warms the shared HTTP
 * cache, and those chunks are already served `immutable, max-age=31536000`.
 *
 * ⚠️ ONE PATH IS NOT A MODAL, and it is what this removal actually costs.
 * components/Analytics/CampaignAnalyticsPage.jsx:412 does
 * `navigate(\`/popup?id=${campaignId}\`)` — a plain react-router navigation that
 * renders <Popup> in THIS document. That click now waits on the editor chunks
 * instead of finding them warm. Accepted deliberately: it is one entry point behind
 * two navigations, against a cost every merchant paid on every boot of every route.
 * If it ever needs to be fast, warm it from that component on hover/intent — not
 * from module scope here, where it cannot be scoped to the people who will use it.
 *
 * Shopify documents the same thing from the other side: for an app window/modal
 * `src`, "the URL is loaded in an iframe WHEN THE WINDOW OPENS". Nothing is
 * fetched for it at render time, so there is nothing to get a head start on.
 *
 * Measured cost of the line, from `yarn build` output (minified, uncompressed):
 *   popup-chunk           125 kB
 *   PopupSettings-chunk   194 kB
 *   Preview-chunk         427 kB   → ~746 kB, ~363 kB gzipped
 * Observed in production before removal: 12 of the 24 app assets fetched on "/"
 * belonged to the editor, and they fetched even on an UNAUTHENTICATED load —
 * which is the proof that they are module-eval side effects, not route loads.
 *
 * (Earlier drafts of this comment cited 3.4MB/684KB — those were SOURCE directory
 * sizes, not shipped bytes. Quote the build output, not `du`.)
 *
 * The editor still warms itself: popup.jsx:18-19 run inside the modal's own
 * document, which is where they are correct and where they were always aimed.
 *
 * `index` is warmed on the two paths that render it, and NOT inside the editor
 * iframe, where it would be the mirror image of the bug above. welcome.jsx
 * navigates to "/" 900ms after the sync succeeds, so the plan-activation path
 * still needs it — suppressing it there would just move a spinner from the front
 * of the flow to the back.
 *
 * .finally, NOT .then — a `.then` callback is skipped on rejection, so one flaky
 * chunk request would leave the dashboard chunk un-warmed too, turning a single
 * harmless failure into two. A failed warm-up is genuinely harmless because the
 * Suspense boundaries below still load on demand; it just must not cascade.
 */
const IS_CAMPAIGN_EDITOR =
  typeof window !== "undefined" && window.location.pathname === "/popup";

/*
 * The template library is its own landing route, so it gets the same treatment as the
 * editor above: warm ITS chunk, not the dashboard's.
 *
 * Before the table below, a cold load of /templates warmed `routes/index` — a route that
 * will never render on this document — and that download competed with the 250 kB/15-chunk
 * templates closure the merchant is actually waiting on. That directly contradicted the
 * "WARM ONLY THE ROUTE THIS DOCUMENT WILL ACTUALLY RENDER" rule stated above.
 *
 * ⚠️ This const is still LOAD-BEARING even though the warm-up no longer branches on it:
 * the /templates <Suspense fallback> at :649 uses it to tell a COLD landing (draw the same
 * BootShell index.html already painted) from an IN-APP transition (draw the grid skeleton).
 */
const IS_TEMPLATE_LIBRARY =
  typeof window !== "undefined" && window.location.pathname === "/templates";

/*
 * The landing pathname, trailing slash normalised away.
 *
 * react-router treats "/analytics" and "/analytics/" as the same route, so the table below
 * must too — otherwise a trailing slash silently falls through to the dashboard fallback,
 * which is the exact bug this table exists to remove.
 */
const WARM_PATHNAME =
  typeof window !== "undefined"
    ? window.location.pathname.replace(/\/+$/, "") || "/"
    : "/";

/*
 * PATHNAME → the chunk THIS document will actually render.
 *
 * Before this table the else-branch warmed `routes/index` for every path it did not
 * recognise, so a cold load of /analytics, /integrations, /billing, /survey-results,
 * /ga4-setup or /data-privacy downloaded the dashboard closure it would never mount —
 * competing for bandwidth with the chunk the merchant was waiting on. Same violation of
 * the "WARM ONLY THE ROUTE THIS DOCUMENT WILL ACTUALLY RENDER" rule as the /templates
 * case above, just spread across six more routes.
 *
 * Thunks, not paths: each `import()` specifier must stay a STATIC string literal or Vite
 * cannot resolve it at build time and the chunk is never split out.
 *
 * ⚠️ `/popup` is deliberately ABSENT — see the long note above; warming the editor from
 * module scope was the 2026-08-16 regression. Keying on the document's own pathname makes
 * that safe by construction: the parent admin realm's document is never /popup, and the
 * IS_CAMPAIGN_EDITOR guard below keeps the editor iframe from warming anything at all.
 *
 * ⚠️ `/welcome` is also absent, and that is not an omission: it needs the TWO-STEP warm
 * in the branch below (welcome, then index, because welcome.jsx navigates to "/" 900 ms
 * after the sync succeeds), which a single-thunk table cannot express.
 */
const ROUTE_WARMERS = {
  "/": () => import("../routes/index"),
  "/templates": () => import("../routes/templates"),
  "/billing": () => import("../routes/billing"),
  "/integrations": () => import("../routes/integrations"),
  "/survey-results": () => import("../routes/survey-results"),
  "/data-privacy": () => import("../routes/data-privacy"),
  "/analytics": () => import("../routes/analytics"),
  "/ga4-setup": () => import("../routes/ga4-setup"),
};

/*
 * Parameterised routes, matched by PREFIX.
 *
 * ⚠️ /analytics/campaigns/:campaignId CANNOT go in the exact-match table — the id is part
 * of the pathname, so every real URL misses and drops to the fallback, which would keep
 * warming the dashboard on the second-heaviest route in the app (128.4 kB gz, 89% of it
 * vendor-charts). Checked only after the exact table, so "/analytics" itself still wins.
 */
const PREFIX_WARMERS = [
  ["/analytics/campaigns/", () => import("../routes/campaign-analytics")],
];

if (IS_PLAN_ACTIVATION_RETURN) {
  import("../routes/welcome")
    .catch(() => {})
    .finally(() => {
      import("../routes/index").catch(() => {});
    });
} else if (!IS_CAMPAIGN_EDITOR) {
  const warmRoute =
    ROUTE_WARMERS[WARM_PATHNAME] ||
    PREFIX_WARMERS.find(([prefix]) => WARM_PATHNAME.startsWith(prefix))?.[1] ||
    // Unknown path — react-router renders Error404, whose only forward paths are both the
    // dashboard: it navigates to "/" outright when the pathname is the app URL's own
    // (:592-599), and otherwise offers a single "Go to Dashboard" button. So `index` is
    // still the right guess here — it is the wrong guess only for the routes above, which
    // is what the table fixes.
    ROUTE_WARMERS["/"];
  warmRoute().catch(() => {});
}

/**
 * ATTACH THE POLARIS COMPONENT STYLESHEET ON EVERY ROUTE, IN EVERY REALM.
 *
 * The two `lazy()` factories above await `polarisCssReady` so that `/` and `/popup` cannot
 * COMMIT before the sheet is applied — that gating is what prevents a flash of unstyled
 * Polaris on the two heaviest screens, and it must stay.
 *
 * But gating is not the same as loading, and every other route renders Polaris React too:
 * `/analytics` and `/ga4-setup` use `Tabs` (and `Modal`), `/templates` uses
 * `SkeletonThumbnail`, `/billing` and `/integrations` render `PageSkeleton`'s
 * `SkeletonDisplayText`/`SkeletonBodyText`. Before this line, a merchant who deep-linked
 * to one of those — or landed in the editor's iframe realm, which has its own module cache —
 * got the stylesheet DOWNLOADED by the preload tag and never applied, because
 * `polarisCss.js` was reachable only from the dashboard's factory.
 *
 * This bare side-effect import runs at module-eval, so the attach happens once per realm no
 * matter which route wins. It is dynamic (not a static top-level import) to keep it out of
 * the entry chunk's static graph, and `.catch` because a failed stylesheet must degrade to
 * unstyled, never to a boot failure — the same contract polarisCss.js already honours
 * internally by resolving on `error` and on a 3 s timeout.
 *
 * Cheap by construction: `polarisCss.js` is idempotent (it marks its own <link> with
 * `data-ns-polaris-css-loaded` and returns the memoised promise), so this and the two
 * factories above cannot race into duplicate stylesheets.
 */
import("../utils/polarisCss").catch(() => {});
// Hidden for initial launch — uncomment to re-enable experiments
// const ExperimentPage = lazy(() => import("../routes/experiment"));
// const ExperimentsPage = lazy(() => import("../routes/experiments"));
const IntegrationsPage = lazy(() => import("../routes/integrations"));
const SurveyResultsPage = lazy(() => import("../routes/survey-results"));
// Shared targeting audiences. NOT added to EARLY_MOUNT_PATHS: that carries a `!shop` render-hold
// obligation, and these pages have no reason to mount before the shop lands.
const TargetingRulesPage = lazy(() => import("../routes/targeting-rules"));
const TargetingRuleDetailPage = lazy(() => import("../routes/targeting-rule-detail"));
/**
 * `primeAppHandle()` runs in the FACTORY, not inside the route.
 *
 * routes/billing.jsx needs the Shopify app handle to build the hosted-pricing URL.
 * It used to request it from a mount effect, i.e. strictly AFTER this chunk had
 * downloaded, parsed and committed. Firing it here overlaps that round trip with the
 * chunk download instead.
 *
 * ⚠️ NOT a `Promise.all` — deliberately. Awaiting the handle here would gate the
 * route's mount on it, which is the opposite of the point: the page renders fine
 * without it (the "View plans in Shopify" button just stays in its loading state
 * until it lands). primeAppHandle also never rejects, so it cannot fail this factory.
 *
 * ⚠️ The `primeAppHandle` import is STATIC on purpose. A dynamic import here would
 * cost its own chunk request before the action could even be called, which is the
 * round trip this is trying to remove. The module only imports `../api`, which this
 * file already imports.
 */
const BillingPage = lazy(() => {
  primeAppHandle();
  return import("../routes/billing");
});
const WelcomePage = lazy(() => import("../routes/welcome"));
const DataPrivacyPage = lazy(() => import("../routes/data-privacy"));
const GA4SetupPage = lazy(() => import("../routes/ga4-setup"));
const AnalyticsPage = lazy(() => import("../routes/analytics"));
/**
 * The data request starts in the FACTORY, in parallel with the chunk download.
 *
 * 89% of this route's 128.4 kB gz is `vendor-charts`, and the page used to fetch from a
 * mount effect — so the fetch could not start until ~114 kB of recharts had downloaded
 * and parsed. Everything the request needs (campaign id, timeframe) is already in the
 * URL, so it can start now. The page consumes the in-flight promise; if it is missing or
 * the key has moved on, it just fetches normally.
 *
 * ⚠️ NOT awaited / NOT a Promise.all — the chunk must not wait on the data.
 * `primeFromLocation` swallows its own errors, so it can never fail this factory.
 */
const CampaignAnalyticsPage = lazy(() => {
  primeFromLocation(window.location.pathname, window.location.search);
  return import("../routes/campaign-analytics");
});
/*
 * Same shape as CampaignAnalyticsPage above: start the data request INSIDE the factory
 * rather than from a mount effect, so it does not queue behind the route module's own parse.
 * The catalogue view is ~337 ms and nothing used to call useDefaultTemplates until
 * routes/templates had finished evaluating.
 *
 * ⚠️ Scope of the win, precisely: this factory runs only when React actually renders
 * <TemplatesPage/>, which is behind three gates — App's setTimeout(0) router gate,
 * AuthenticatedApp's auth gate, and ShopProvider's shop gate. So on a COLD load the prime
 * fires after auth and shop have already resolved, and the chunk is already downloading from
 * the module-eval warm-up above; the two do not race there. The real gain is on an IN-APP
 * TRANSITION (dashboard → templates), where the gates are long since satisfied and this is
 * the earliest possible moment to ask for the data.
 *
 * primeDefaultTemplates swallows its own errors, so it can never fail this factory.
 */
/*
 * `primeAiShowcase` rides along for the same reason, but wins MORE than its neighbour does.
 *
 * `useDefaultTemplates`' consumer lives in the templates route chunk itself — one hop away —
 * so the note above is right that a cold load barely races there. The AI showcase request is
 * two lazy chunks deep: <TemplateLibrary> lazy-loads <AIGeneratorCard>, whose own chunk is
 * only 2.5 kB gz but which sits behind ~156 kB gz of transitively-imported renderer JS.
 * Fired from a mount effect, the request could not START until all of that had downloaded
 * and evaluated. From here it overlaps the whole chain — on a cold load, not just a
 * transition.
 *
 * A failed prime cannot make things worse: useAiShowcase never caches a failure, so it can
 * never poison the session with an empty result — the strip just hides for this page view,
 * exactly as it did before, and the next load starts clean. See the note on
 * `loadAiShowcase` for why "retries" would be too strong a word here.
 */
const TemplatesPage = lazy(() => {
  primeDefaultTemplates();
  primeAiShowcase();
  return import("../routes/templates");
});
import { api } from "../api";
import { primeAppHandle } from "../utils/appHandle";
import { primeFromLocation } from "../utils/campaignAnalyticsPrefetch";
// Static, matching primeAppHandle's rationale: the module pulls only `react` and `../api`,
// both already in the entry graph, so this adds no new eager weight.
import { primeDefaultTemplates } from "../hooks/useDefaultTemplates";
// Same rationale: the module pulls only `react`, already in the entry graph, so this adds no
// eager weight. It does not even import `../api` — the showcase route is a plain `fetch`.
import { primeAiShowcase } from "../hooks/useAiShowcase";
import { ShopProvider } from "../providers";
import RouteLoader from "./RouteLoader";
import PlanActivationScreen from "./PlanActivationScreen";
import { markBoot } from "../utils/bootTiming";
/**
 * The boot shell. Every gate between the first paint and the real page renders THIS —
 * the same picture index.html already painted statically inside #root — so the whole
 * boot is one unchanging frame instead of blank → spinner → skeleton.
 *
 * RouteLoader (a spinner) is still correct for genuine in-app route TRANSITIONS on the
 * other routes: at that point the merchant is already looking at a painted admin and a
 * spinner reads as "working", not as "broken".
 */
import BootShell, { BOOT_SHELL_VARIANT, bootShellVariant } from "./BootShell";
import CampaignEditorSkeleton from "./CampaignEditor/CampaignEditorSkeleton";
// Eager on purpose, like CampaignEditorSkeleton above: a Suspense fallback that is itself
// lazy would render nothing on the frame it is needed. It is a few hundred bytes.
import BillingSkeleton from "./BillingSkeleton";
import IntegrationsSkeleton from "./IntegrationsSkeleton";
// Same reasoning — these are Suspense fallbacks, so they must already be in memory on the
// frame the route chunk is still arriving. Each is a few hundred bytes of grey boxes.
//
// ⚠️ `Ga4SetupSkeleton` — note the casing. A `GA4SetupSkeleton` spelling was written and
// silently merged into this same file on macOS, which is case-INSENSITIVE. Gadget builds on
// Linux, which is not: the wrong casing resolves locally and fails the deploy. Match the
// filename exactly.
import SurveyResultsSkeleton from "./SurveyResultsSkeleton";
import DataPrivacySkeleton from "./DataPrivacySkeleton";
import AnalyticsSkeleton from "./AnalyticsSkeleton";
import CampaignAnalyticsSkeleton from "./CampaignAnalyticsSkeleton";
import Ga4SetupSkeleton from "./Ga4SetupSkeleton";
import TemplateLibrarySkeleton from "./TemplateLibrarySkeleton";

/**
 * ChatWidget is LAZY. It was a static import here and rendered unconditionally below, so
 * `tawk`, `Tawk` and `chat-widget-facade` all shipped inside the ENTRY chunk — the one
 * thing every merchant downloads and parses before anything can paint, on every route,
 * including the campaign editor. Nothing about a support-chat bubble belongs there.
 *
 * `fallback={null}` because the facade is a floating bubble in the corner: there is no
 * layout to reserve and nothing to shift when it appears a moment later.
 */
const ChatWidget = lazy(() => import("./ChatWidget"));

/* ===========================================================================
 * OPTIMISTIC INSTALL-STATE HINT
 *
 * THE PROBLEM. `@gadgetinc/react-shopify-app-bridge`'s Provider always fires one
 * `fetchOrInstallShop` mutation at boot, and with no prior knowledge it renders
 * `loading = true` until that mutation comes back (Provider.js:127-137 — the
 * `else { loading = true; }` branch). That is a full serial round trip in front of
 * EVERYTHING: the shop query cannot start, the campaigns query cannot start, nothing
 * paints but the shell. A no-op Gadget GraphQL query measures 320 ms p50 / 2.1 s p90
 * in production, so this alone is 0.3–2 s of skeleton on every single boot.
 *
 * THE ESCAPE HATCH. The Provider already supports being told the answer up front —
 * `shopifyInstallState` is a documented prop (Provider.d.ts:26) and it falls back to
 * `window.gadgetConfig?.shopifyInstallState` (Provider.js:67). With a hint that says
 * `isAuthenticated`, `loading` is false on the FIRST render and the mutation stops
 * blocking. Gadget did not populate that global for this app — the embedded frame
 * pointed at /api/shopify/install-or-render, which 302s to bare "/" and drops the
 * Shopify query params, so the server never got to stamp the state into the HTML
 * (0 hits for `shopifyInstallState` in the served production HTML). So we supply it.
 *
 * ⚠️ UPDATED 2026-08-18: `application_url` is now the ROOT path, so Shopify's query params
 * DO reach the server and Gadget may now populate that global natively. This hint is
 * therefore belt-and-braces rather than the only source. It does NOT shadow the native
 * value on a cold cache: `getInstallStateHint()` returns `undefined` when there is no
 * cached clean-yes, and an undefined prop falls through to Provider.js:67's global.
 * Re-measure before deciding this block is dead — if `shopifyInstallState` now appears in
 * the served HTML on every boot, this whole mechanism can go, but that is a SEPARATE
 * change with its own paywall risk (ShopProvider.jsx:521) and must not be bundled.
 *
 * ⚠️ THIS REMOVES THE *BLOCKING*, NEVER THE CHECK. The mutation still runs on every
 * boot exactly as before, and its answer still wins: Provider.js:70-79 prefers
 * `fetchOrInstallShopData` over the hint whenever it is present, and Provider.js:146-158
 * pushes the corrected `isAuthenticated` into the context from an effect. A wrong hint
 * costs a re-render, not a wrong authorisation.
 *
 * ⚠️ localStorage, NOT sessionStorage. sessionStorage is per TAB, and a fresh tab is the
 * common merchant entry (they click the app in the admin sidebar), so a session-scoped
 * cache would be cold on almost every boot that matters — the optimisation would
 * essentially never fire. localStorage is only safe here BECAUSE the key carries the shop
 * domain: one shop's state can never be replayed for another.
 *
 * ⚠️ Every access is wrapped — Safari private mode throws on write, and a corrupt entry
 * must degrade to the slow-but-correct boot, never to a crash.
 * =========================================================================== */
const INSTALL_STATE_KEY_PREFIX = "ns_shopify_install_state:";

/**
 * The localStorage key for THIS shop, or null when we cannot name the shop.
 *
 * `window.shopify.config.shop` is the App Bridge global's shop origin — "not provided by
 * the app developer", i.e. Shopify fills it in (@shopify/app-bridge-types/dist/shopify.ts,
 * AppBridgeConfig.shop). app-bridge.js is a blocking classic script in the document head
 * and our entry is a deferred module, so it is already there by the time anything here
 * runs, on both the read and the write side — the two therefore always agree on the key.
 *
 * ⚠️ THE APP BRIDGE GLOBAL IS THE ONLY SOURCE, AND THAT IS A SECURITY PROPERTY, NOT
 * LAZINESS. The `?shop=` query param looks like a reasonable fallback and is not: the
 * Provider picks its inner implementation on `!!globalThis.shopify` (Provider.js:231,254),
 * and the NON-App-Bridge branch — StandaloneInnerProvider — reads
 * `isAuthenticated: shopifyInstallState?.isAuthenticated ?? false` with `loading: false`
 * and NEVER RUNS THE CORRECTING MUTATION. Feeding a hint into that branch would be a
 * genuinely unchecked authentication. Keying on the App Bridge global means the hint can
 * only ever reach InnerGadgetProvider, which always fires `fetchOrInstallShop` and always
 * overrides us. It also means no hint is read or written on a root-frame request.
 */
function installStateKey() {
  if (typeof window === "undefined") return null;
  let domain = null;
  try {
    domain = window.shopify?.config?.shop || null;
  } catch {
    domain = null;
  }
  return domain ? INSTALL_STATE_KEY_PREFIX + domain : null;
}

// Memoised so the object IDENTITY is stable across renders: Provider.js:86-88 lists
// `shopifyInstallState` in an effect dependency array, and a fresh object every render
// would re-run it for nothing.
let installStateHint;
let installStateHintComputed = false;

function getInstallStateHint() {
  if (installStateHintComputed) return installStateHint;
  installStateHintComputed = true;

  const key = installStateKey();
  if (!key) return undefined;

  try {
    const cached = JSON.parse(window.localStorage.getItem(key) || "null");
    // Only a CLEAN yes is worth replaying. A hint carrying `redirectToOauth` or
    // `missingScopes` would have the Provider skip its loading state on precisely the
    // boots that need to redirect or re-consent, so those are never cached through —
    // they fall back to the honest, blocking path.
    if (
      cached?.isAuthenticated === true &&
      cached.redirectToOauth !== true &&
      !(cached.missingScopes?.length > 0)
    ) {
      installStateHint = {
        redirectToOauth: false,
        isAuthenticated: true,
        missingScopes: [],
        shopExists: true,
      };
    }
  } catch {
    // Unreadable or unparseable — boot the slow, correct way.
  }

  return installStateHint;
}

function rememberInstallState() {
  const key = installStateKey();
  if (!key) return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        redirectToOauth: false,
        isAuthenticated: true,
        missingScopes: [],
        shopExists: true,
      })
    );
  } catch {
    // Safari private mode throws on write. The hint is an optimisation, not state —
    // losing it costs one blocking boot and nothing else.
  }
}

function forgetInstallState() {
  const key = installStateKey();
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Same as above — nothing to recover from.
  }
}

function Error404() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (
      process.env.GADGET_PUBLIC_SHOPIFY_APP_URL &&
      location.pathname ===
        new URL(process.env.GADGET_PUBLIC_SHOPIFY_APP_URL).pathname
    )
      return navigate("/", { replace: true });
  }, [location.pathname]);

  return (
    <s-page heading="Page not found">
      <s-section accessibilityLabel="Page not found">
        <s-stack direction="block" gap="base" alignItems="center" padding="large">
          <s-heading>This page doesn't exist</s-heading>
          <s-paragraph>The page you're looking for can't be found. Head back to the dashboard to continue.</s-paragraph>
          <s-button onClick={() => navigate("/")}>Go to Dashboard</s-button>
        </s-stack>
      </s-section>
    </s-page>
  );
}

// Safari-specific router initialization
let routerInstance = null;

function getRouter() {
  if (!routerInstance) {
    routerInstance = createBrowserRouter(
      createRoutesFromElements(
        <Route path="/" element={<Layout />}>
          <Route path="/" index element={
            <Suspense fallback={<BootShell variant="dashboard" />}>
              <Index />
            </Suspense>
          } />
          {/*
            COLD LANDING vs IN-APP TRANSITION — deliberately two different fallbacks.

            On a COLD landing, four gates in a row (the router gate, the auth gate, the shop
            gate, and index.html's static markup) all draw the generic BootShell. RouteLoader
            was the fifth and the only one that broke the sequence, so a merchant deep-linking
            to /templates watched the shell get REPLACED by a differently-shaped loader while
            the 250 kB route closure downloaded. Drawing the same shell keeps the whole boot
            one unchanging picture, which is the rule stated above the BootShell import.

            On an in-app TRANSITION the merchant is already looking at a painted admin, and a
            full-page shell there reads as a reload. RouteLoader is correct in that case — see
            the same comment. IS_TEMPLATE_LIBRARY is fixed at module-eval from the LANDING
            pathname, so it distinguishes the two without any per-render work.
          */}
          {/* Two different waits, two different right answers.
              COLD landing (IS_TEMPLATE_LIBRARY): index.html has already painted the GENERIC
              static shell with zero JS, so React must draw that same shape or the merchant
              sees the picture change under them. Keep BootShell.
              IN-APP transition: nothing has been painted for this route yet, so there is no
              earlier shape to match — draw the template grid itself. */}
          <Route path="/templates" element={
            <Suspense fallback={IS_TEMPLATE_LIBRARY ? <BootShell variant="generic" /> : <TemplateLibrarySkeleton />}>
              <TemplatesPage />
            </Suspense>
          } />
          <Route path="/popup" element={
            <Suspense fallback={<CampaignEditorSkeleton />}>
              <Popup />
            </Suspense>
          } />
          {/*
            BillingSkeleton, NOT RouteLoader — and routes/billing.jsx renders the SAME
            component for its own `fetching` state. /billing used to show two different
            spinners (this one large and centred, then a smaller one under a heading) for a
            single wait. Rendering one picture at both gates makes the handoff invisible.
            See the header of components/BillingSkeleton.jsx.
          */}
          <Route path="/billing" element={
            <Suspense fallback={<BillingSkeleton />}>
              <BillingPage />
            </Suspense>
          } />
          {/*
            Shopify App Pricing return landing. Set as the per-plan "Redirect URL"
            in the Partner Dashboard, so this path MUST exist before App Pricing
            is enabled — otherwise every merchant who pays lands on Error404,
            which does not auto-redirect (:47-54 only fires for install-or-render).
          */}
          <Route path="/welcome" element={
            <Suspense fallback={<PlanActivationScreen />}>
              <WelcomePage />
            </Suspense>
          } />
          {/* Hidden for initial launch — uncomment to re-enable experiments
          <Route path="/experiments" element={
            <Suspense fallback={<RouteLoader />}>
              <ExperimentsPage />
            </Suspense>
          } />
          <Route path="/experiments/:experimentId" element={
            <Suspense fallback={<RouteLoader />}>
              <ExperimentPage />
            </Suspense>
          } />
          */}
          {/* Same one-picture rule as /billing above — routes/integrations.jsx renders this
              exact component for its own fetching state. See components/PageSkeleton.jsx. */}
          <Route path="/integrations" element={
            <Suspense fallback={<IntegrationsSkeleton />}>
              <IntegrationsPage />
            </Suspense>
          } />
          {/* Each route below draws ITS OWN page's shape while its chunk loads, rather
              than the generic centred spinner RouteLoader used to show. A spinner makes
              no promise; a skeleton that matches the page it precedes turns the wait into
              a preview. (Which is also why a MISMATCHED skeleton is worse than a spinner —
              it promises a layout and then jumps. Each of these mirrors the real route,
              and the per-component comments record what they were matched against.) */}
          <Route path="/survey-results" element={
            <Suspense fallback={<SurveyResultsSkeleton />}>
              <SurveyResultsPage />
            </Suspense>
          } />
          {/* Survey Results ALSO lives as a tab inside /analytics. The standalone route stays
              registered on purpose: deep links and bookmarks must not 404. */}
          <Route path="/targeting-rules" element={
            <Suspense fallback={null}>
              <TargetingRulesPage />
            </Suspense>
          } />
          {/* `/new` is declared BEFORE `/:id` so it is matched as a literal, not as an id. */}
          <Route path="/targeting-rules/new" element={
            <Suspense fallback={null}>
              <TargetingRuleDetailPage />
            </Suspense>
          } />
          <Route path="/targeting-rules/:id" element={
            <Suspense fallback={null}>
              <TargetingRuleDetailPage />
            </Suspense>
          } />
          <Route path="/data-privacy" element={
            <Suspense fallback={<DataPrivacySkeleton />}>
              <DataPrivacyPage />
            </Suspense>
          } />
          <Route path="/analytics" element={
            <Suspense fallback={<AnalyticsSkeleton />}>
              <AnalyticsPage />
            </Suspense>
          } />
          <Route path="/analytics/campaigns/:campaignId" element={
            <Suspense fallback={<CampaignAnalyticsSkeleton />}>
              <CampaignAnalyticsPage />
            </Suspense>
          } />
          <Route path="/ga4-setup" element={
            <Suspense fallback={<Ga4SetupSkeleton />}>
              <GA4SetupPage />
            </Suspense>
          } />
          <Route path="*" element={<Error404 />} />
        </Route>
      )
    );
  }
  return routerInstance;
}

function App() {
  const [router, setRouter] = useState(null);
  
  useEffect(() => {
    // ⚠️ DO NOT "SIMPLIFY" THIS INTO A RENDER-PHASE `getRouter()` CALL.
    //
    // It reads like redundant scaffolding on top of the module-level singleton.
    // It is not — it is the third attempt at SEI-64, and the first two are in
    // the history:
    //
    //   76f52eb4  removed the useMemo AND moved the router to module scope,
    //             under the comment "Create router outside of React components
    //             for Safari compatibility"          → STILL BROKEN in Safari
    //   af42f23c  tore module scope back out and deferred it via setTimeout
    //                                                 → fixed
    //
    // So: useMemo failed, module scope failed, deferral worked. Calling
    // getRouter() during render is functionally the module-scope form that
    // already failed, so that "cleanup" reintroduces a shipped bug.
    //
    // (vite.config.mjs:22-26 notes that `dedupe: ["react","react-dom"]` was a
    // silent no-op until 2026-08, and duplicate React is the classic cause of
    // "useMemo was causing issues" — so the underlying bug may now be fixed for
    // real. That is a hypothesis. The cost of this timer is one macrotask;
    // the cost of being wrong is a Safari-only regression in an embedded admin
    // app. Not a trade worth making without evidence.)
    const timer = setTimeout(() => {
      setRouter(getRouter());
    }, 0);

    return () => clearTimeout(timer);
  }, []);
  
  if (!router) {
    // ⚠️ This gate is ABOVE RouterProvider, so there is NO router context here —
    // it must use the module constant, never useLocation(). AuthenticatedApp
    // below sits inside the router and correctly uses useLocation() instead.
    // That difference is not an inconsistency to tidy up: each is the only
    // correct option at its position, and "unifying" them breaks this one.
    //
    // BOOT_SHELL_VARIANT is exactly that module constant (BootShell.jsx reads
    // window.location.pathname at module scope). It renders the SAME picture the
    // static shell in index.html has already painted, so React's first commit —
    // which clears #root — swaps like for like instead of replacing the shell
    // with a spinner.
    return IS_PLAN_ACTIVATION_RETURN ? (
      <PlanActivationScreen />
    ) : (
      <BootShell variant={BOOT_SHELL_VARIANT} />
    );
  }

  return <RouterProvider router={router} />;
}

export default App;

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  // Try to get Shopify API key from various sources
  let shopifyApiKey = window.gadgetConfig?.apiKeys?.shopify || 
                      window.gadget?.config?.apiKeys?.shopify ||
                      process.env.GADGET_PUBLIC_SHOPIFY_API_KEY;
  
  if (!shopifyApiKey) {
    console.error("Missing Shopify API key configuration. Available sources:", {
      gadgetConfig: !!window.gadgetConfig,
      gadgetConfigApiKeys: !!window.gadgetConfig?.apiKeys,
      gadgetObject: !!window.gadget,
      envVar: !!process.env.GADGET_PUBLIC_SHOPIFY_API_KEY
    });
    
    return (
      <s-page>
        <s-paragraph>
          Loading configuration... If this persists, please refresh the page.
        </s-paragraph>
      </s-page>
    );
  }

  const config = {
    type: AppType.Embedded,
    shopifyApiKey: shopifyApiKey,
    api: api,
    // See the OPTIMISTIC INSTALL-STATE HINT block above. `undefined` on a shop we have
    // never successfully booted for — which is exactly what the Provider defaults to —
    // so this is a no-op rather than a behaviour change when there is no hint.
    shopifyInstallState: getInstallStateHint(),
  };

  return (
    <GadgetProvider {...config}>
      <AuthenticatedApp />
    </GadgetProvider>
  );
}

function AuthenticatedApp() {
  // we use `isAuthenticated` to render pages once the OAuth flow is complete!
  //
  // ⚠️ `error` was destructured NOWHERE in web/ until 2026-08-16. The Provider sets it
  // from `fetchingOrInstallingShopError || idTokenError` (Provider.js:146-158), and an
  // idToken() failure in particular never flips `loading` back to false — the mutation is
  // simply never dispatched, so `hasStartedFetchingOrInstallingShop` stays false and
  // Provider.js:127-137 pins `loading = true`. The merchant sat on the boot shell forever,
  // with no message and nothing logged. See the terminal branch below.
  const { isAuthenticated, loading, error } = useGadget();
  const location = useLocation();

  /**
   * Maintain the optimistic install-state hint (see the block above Error404).
   *
   * ⚠️ WE ONLY WRITE WHEN NO HINT WAS USED THIS BOOT. With a hint in play the Provider
   * reports `isAuthenticated` from the HINT until the mutation lands, so writing then
   * would refresh the cache from itself — a stale entry would re-confirm forever and
   * could never expire. Skipping the write when `getInstallStateHint()` is truthy means
   * every entry in localStorage traces back to a real backend answer.
   *
   * The clear path has no such ambiguity and always applies: `isAuthenticated === false`
   * while settled can ONLY come from the mutation (Provider.js:70-79 prefers the mutation
   * data over the hint whenever it exists), so the backend has genuinely disagreed and the
   * hint must go.
   *
   * No cleanup function and no ref guard — deliberately. The pair deadlocks under
   * StrictMode (.claude/rules/frontend.md), and neither is needed: both calls are
   * synchronous, idempotent and safe to repeat.
   */
  useEffect(() => {
    if (error) {
      forgetInstallState();
      return;
    }
    if (loading) return;
    if (!isAuthenticated) {
      forgetInstallState();
      return;
    }
    if (getInstallStateHint()) return;
    rememberInstallState();
  }, [loading, isAuthenticated, error]);

  /**
   * TERMINAL AUTH FAILURE — must sit ABOVE the loading branch.
   *
   * "Still loading" and "broken" are indistinguishable to the person watching, and this
   * particular break does not resolve: on an idToken() failure `loading` never goes false
   * (see the destructure above), so without this the merchant watches the boot shell for
   * the rest of the session. Same shape, and the same reasoning, as the settled-but-empty
   * branch in providers/ShopProvider.jsx.
   *
   * Reload rather than a retry callback: the Provider's fetch is guarded by a
   * `hasFetchedOrInstalledShop` ref (Provider.js:90-92) that is never reset, so there is
   * no in-place way to make it try again — a fresh document is the only real retry.
   */
  if (error) {
    return (
      <s-page heading="We couldn't verify your session">
        <s-section>
          <s-stack direction="block" gap="base">
            <s-banner tone="critical" heading="Something went wrong">
              <s-text>
                {error?.message ??
                  "We couldn't confirm your Shopify session. Reload to try again."}
              </s-text>
            </s-banner>
            <s-stack direction="inline" justifyContent="start">
              <s-button variant="primary" onClick={() => window.location.reload()}>
                Try again
              </s-button>
            </s-stack>
          </s-stack>
        </s-section>
      </s-page>
    );
  }

  if (loading) {
    // Unlike the router gate in App(), this renders INSIDE the router (Layout is
    // a route element — EmbeddedApp below calls useLocation() at the same
    // depth), so read the live path rather than the boot-time constant:
    // `loading` can re-fire after a later navigation, at which point a
    // module-scope constant would be stale.
    return location.pathname === "/welcome" ? (
      <PlanActivationScreen />
    ) : (
      <BootShell variant={bootShellVariant(location.pathname)} />
    );
  }

  // Boot timing mark — see utils/bootTiming.js for why performance.mark and not
  // console.* (console calls are stripped from production builds).
  markBoot("auth_ready");

  return isAuthenticated ? <EmbeddedApp /> : <UnauthenticatedApp />;
}

function EmbeddedApp() {
  const location = useLocation();
  const isPopup = location.pathname === '/popup';

  return (
    <ShopProvider>
      {!isPopup && (
        <NavMenu>
          <Link to="/" rel="home">
            Home
          </Link>
          <Link to="/templates">Template Library</Link>
          <Link to="/billing">Billing</Link>
          <Link to="/integrations">Integrations</Link>
          <Link to="/targeting-rules">Targeting Rules</Link>
          <Link to="/analytics">Analytics</Link>
          {/* <Link to="/ga4-setup">Google Analytics</Link> */}
          {/* Survey Results moved INTO /analytics as a tab to free a slot — Shopify caps app
              navigation at 7 single-level items and we were at exactly 7. The /survey-results
              ROUTE is still registered, so existing links keep working. */}
          <Link to="/data-privacy">Data Privacy</Link>
        </NavMenu>
      )}
      <Outlet />
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </ShopProvider>
  );
}

function UnauthenticatedApp() {
  return (
    <s-page heading="App">
      <s-paragraph>
        App can only be viewed in the Shopify Admin.
      </s-paragraph>
    </s-page>
  );
}
